import { Form, Head } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import BrandCoverImageUploader from "@/components/brand/brand-cover-image-uploader"
import BrandLogoUploader from "@/components/brand/brand-logo-uploader"
import BrandSplashCoverImageUploader from "@/components/brand/brand-splash-cover-image-uploader"
import BrandSettingsLayout from "@/components/brand/settings/brand-settings-layout"
import BrandTimezoneSelect from "@/components/brand/settings/brand-timezone-select"
import HeadingSmall from "@/components/heading-small"
import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import AppLayout from "@/layouts/app-layout"
import { brandSettingsProfilePath } from "@/routes"

/**
 * OKS-40 — Brand Settings → Profile page.
 *
 * Inertia `<Form method="patch">` posting to
 * `brandSettingsProfilePath()`. Form binding `name="brand[<field>]"`
 * matches BE strong-params allow-list.
 *
 * Multipart-triggered automatically by the presence of
 * `<input type="file" name="brand[logo]">` inside the form.
 *
 * Member-tier (Brand Member / Agency Member assigned) renders the
 * page in read-only mode: file input disabled, save button absent,
 * inputs `readonly`. `can_edit: boolean` from the BE policy gates.
 */

export interface BrandSettingsProfileProps {
  brand: {
    id: number
    name: string
    support_email: string | null
    locale: "en" | "fr"
    timezone: string
    logo_url: string | null
    /** OKS-304 — wide hero cover image (3:1) shown on creator-facing
     *  surfaces (invitation accept, onboarding Welcome, creator home).
     *  null when no cover attached → BrandCoverImageUploader renders
     *  the dark fallback band. */
    cover_image_url: string | null
    /** OKS-857 — portrait 9:16 splash background image shown full-bleed
     *  behind the brand splash overlay (invite landing + brand-switch).
     *  null when none attached → BrandSplashCoverImageUploader renders the
     *  dark fallback band; the splash then falls back to the cover / dark. */
    splash_cover_image_url: string | null
  }
  can_edit: boolean
}

export default function BrandSettingsProfile({
  brand,
  can_edit,
}: BrandSettingsProfileProps) {
  const { t } = useTranslation()

  const fallback = brand.name.slice(0, 2).toUpperCase()

  return (
    <AppLayout>
      <Head title={t("settings.brand.profile.title")} />
      <BrandSettingsLayout>
        <div data-testid="brand-settings-profile" className="space-y-6">
          <HeadingSmall
            title={t("settings.brand.profile.title")}
            description={t("settings.brand.profile.description")}
          />

          <Form
            method="patch"
            action={brandSettingsProfilePath()}
            options={{ preserveScroll: true }}
            className="space-y-6"
            data-testid="brand-settings-profile-form"
          >
            {({ errors, processing }) => (
              <>
                <BrandLogoUploader
                  currentUrl={brand.logo_url}
                  fallback={fallback}
                  readOnly={!can_edit}
                />
                <InputError messages={errors.logo} />

                {/* OKS-304 — Cover image uploader sits between logo and
                    name per UX Spec §2.1. Server validation errors
                    surface inline below via the standard `<InputError />`
                    contract on the `cover_image` key. */}
                <BrandCoverImageUploader
                  currentUrl={brand.cover_image_url}
                  brandName={brand.name}
                  readOnly={!can_edit}
                />
                <InputError messages={errors.cover_image} />

                {/* OKS-857 — portrait 9:16 splash background uploader, its
                    own field directly below the landscape cover (distinct
                    ratio + purpose, UX Spec §2). Server validation errors
                    surface inline on the `splash_cover_image` key. */}
                <BrandSplashCoverImageUploader
                  currentUrl={brand.splash_cover_image_url}
                  brandName={brand.name}
                  readOnly={!can_edit}
                />
                <InputError messages={errors.splash_cover_image} />

                <div className="grid gap-2">
                  <Label htmlFor="brand-name">
                    {t("settings.brand.profile.name_label")}
                    <span
                      className="text-destructive ml-0.5"
                      aria-hidden="true"
                    >
                      *
                    </span>
                  </Label>
                  <Input
                    id="brand-name"
                    name="brand[name]"
                    aria-required="true"
                    aria-invalid={Boolean(errors.name)}
                    defaultValue={brand.name}
                    readOnly={!can_edit}
                    aria-readonly={!can_edit}
                    data-testid="brand-settings-profile-name"
                  />
                  <InputError messages={errors.name} />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="brand-support-email">
                    {t("settings.brand.profile.support_email_label")}
                  </Label>
                  <Input
                    id="brand-support-email"
                    type="email"
                    name="brand[support_email]"
                    defaultValue={brand.support_email ?? ""}
                    readOnly={!can_edit}
                    aria-readonly={!can_edit}
                    data-testid="brand-settings-profile-support-email"
                  />
                  <p className="text-muted-foreground text-xs">
                    {t("settings.brand.profile.support_email_helper")}
                  </p>
                  <InputError messages={errors.support_email} />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="brand-locale">
                    {t("settings.brand.profile.locale_label")}
                  </Label>
                  <Select
                    items={{ fr: "Français", en: "English" }}
                    name="brand[locale]"
                    defaultValue={brand.locale}
                    disabled={!can_edit}
                  >
                    <SelectTrigger
                      id="brand-locale"
                      data-testid="brand-settings-profile-locale"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fr">Français</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                    </SelectContent>
                  </Select>
                  <InputError messages={errors.locale} />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="brand-timezone">
                    {t("settings.brand.profile.timezone_label")}
                  </Label>
                  {/* OKS-188 — full searchable IANA picker (Combobox) with
                      the curated Common group pinned on top. Replaces the
                      old 9-entry hardcoded `<Select>`. Wire contract
                      unchanged (`name="brand[timezone]"`, raw IANA value). */}
                  <BrandTimezoneSelect
                    name="brand[timezone]"
                    id="brand-timezone"
                    testId="brand-settings-profile-timezone"
                    defaultValue={brand.timezone}
                    errorMessages={errors.timezone}
                    errorId="brand-timezone-error"
                    disabled={!can_edit}
                    required
                  />
                  <InputError
                    id="brand-timezone-error"
                    messages={errors.timezone}
                  />
                </div>

                {can_edit ? (
                  <Button
                    type="submit"
                    disabled={processing}
                    data-testid="brand-settings-profile-save"
                  >
                    {processing
                      ? t("settings.brand.profile.saving")
                      : t("settings.brand.profile.save")}
                  </Button>
                ) : (
                  <p
                    role="status"
                    data-testid="brand-settings-profile-readonly"
                    className="text-muted-foreground text-sm"
                  >
                    {t("settings.brand.profile.read_only_hint")}
                  </p>
                )}
              </>
            )}
          </Form>
        </div>
      </BrandSettingsLayout>
    </AppLayout>
  )
}
